import {
    UseMutateFunction,
} from '@tanstack/react-query'
import {
    useCallback, useMemo,
} from 'react'
import useCancelLane from 'app/Apps/AccountManagement/AQuotes/hooks/useCancelLane'
import useRejectPricingLane
    from 'app/Apps/AccountManagement/AQuotes/hooks/useRejectPricingLane'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import {
    LaneDetails,
} from 'app/Apps/Pricing/Pricing.types'
import useRequest, {
    RequestFn, RequestKeys,
} from 'app/hooks/useRequest'

import {
    LaneStatus,
} from 'app/enums/label.enums'
import Button from 'app/shared-components/Button'
import StatusHandler from 'app/shared-components/StatusHandler'
import LaneForm from 'app/Apps/Pricing/LaneGroups/Lanes/LaneForm'
import useHasPermission from 'app/hooks/useHasPermission'
import LaneRequests from 'app/requests/Lane/Lane.requests'
import LaneStatusRequests from 'app/requests/Lane/LaneStatus.requests'

export default function AssignPricingLaneUpdate({
    id,
    refetch,
}: Readonly<{
    id: number,
    refetch: (...args: any[]) => void
}>) {
    const contractBasisUpdatePermission = useHasPermission(['contract_basis_update'])

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useRequest<LaneDetails>({
        key: RequestKeys.getContractBasisLaneGroupLaneDetails,
        params: {
            id,
        },
        requestFunc: RequestFn.getEntity(LaneRequests.get),
        enabled: Boolean(id),
        keepPreviousData: true,
    })

    const onSuccess = useCallback(() => {
        invalidate()
        refetch()
    }, [
        invalidate,
        refetch,
    ])

    const setAsRejected = useRejectPricingLane({
        id, onSuccess,
    })

    const setAsCancelled = useCancelLane({
        id, onSuccess,
    })

    const {
        mutate: setAsPriced,
    }: {
        mutate: UseMutateFunction<void, unknown, void, unknown>
    } = useSimpleApiRequest({
        onSuccess,
        name: 'LaneSetAsPriced',
        requestParams: LaneStatusRequests.setAsPriced({
            id,
        }),
        title: 'Lane has been set as Priced',
    })

    const {
        mutate: removeLaneFromLaneGroup,
    }: {
        mutate: UseMutateFunction<void, unknown, void, unknown>
    } = useSimpleApiRequest({
        onSuccess,
        title: 'Lane has been removed from lane group',
        name: 'RemoveLaneFromLaneGroup',
        requestParams: LaneRequests.removeLaneFromLaneGroup({
            laneId: id,
        }),
    })

    const customButtons = useMemo(() => {
        return [
            <Button
                key="setAsPriced"
                name="setAsPriced"
                label="Set as Priced"
                onClick={() => {
                    return setAsPriced()
                }}
            />,
            <Button
                key="setAsRejectedPricing"
                name="setAsRejectedPricing"
                label="Set as Rejected"
                onClick={() => {
                    return setAsRejected()
                }}
            />,
            <Button
                key="setAsCancelled"
                name="setAsCancelled"
                label="Set as Cancelled"
                onClick={() => {
                    return setAsCancelled()
                }}
            />,
            ...(data?.laneStatus === LaneStatus.IN_PROGRESS ? [(
                <Button
                    key="RemoveLaneFromLaneGroup"
                    name="RemoveLaneFromLaneGroup"
                    label="Remove from Lane Group"
                    onClick={() => {
                        return removeLaneFromLaneGroup()
                    }}
                    secondary
                />
            )] : []),
        ]
    }, [
        data?.laneStatus,
        removeLaneFromLaneGroup,
        setAsCancelled,
        setAsPriced,
        setAsRejected,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <LaneForm
                disabled={!contractBasisUpdatePermission}
                data={data}
                onSuccess={onSuccess}
                onCancel={invalidate}
                requestParam={LaneRequests.update({
                    laneId: id,
                })}
                customButtons={!contractBasisUpdatePermission ? [] : customButtons}
                changeStatusDisabled
            />
        </StatusHandler>
    )
}
